import * as React from 'react';
import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Grid,
  Chip,
  Divider,
  useTheme,
  Autocomplete,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Collapse,
  Button,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import PublicIcon from '@mui/icons-material/Public';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { getATLASFields } from './AtlasValidationPreview';
import { groupFieldsForSection } from './atlasFieldGroups';
import { FieldGroup } from './FieldGroup';
import FieldValidator from './FieldValidator';
import MLPredictionCell from './MLPredictionCell';
import HistoryChip from './HistoryChip';
import { useTranslation, useLanguageStore } from '../store/languageStore';
import { convertToDisplayValue, convertToBackendValue } from '../utils/backendValueConverter';
import codelistService from '../services/codelistService';
import { getLocalizedDescription } from '../utils/codelistTranslations';
import { getMLFieldMapping } from './mlPredictionCategories';
import { parseMultiValue } from './mlPredictionSanitize';
import { evaluateFieldRules, FieldStatus as RuleFieldStatus } from '../services/atlasFieldRules';
import { clearMlBlackout, setApplyingPredictions } from './mlCoordination';
import { resolveFieldTab, buildPredictedFieldPaths, buildDocumentSourcedPaths } from './fieldProvenance';
import { isBulkFillable, pathsNeedingFill } from './bulkPositionFill';
import { resolveCodelistValue } from './resolveCodelistValue';
import {
  resolveFilterTab,
  mergeStickyFields,
  fieldPathFromEventTarget,
  FILTER_TAB_LABEL_KEYS,
} from './fieldTabPlacement';
import type { FilterTabKey } from './fieldTabPlacement';
import { toast } from 'react-toastify';
import { brand } from '../theme';
import { formIcons } from '../assets/icons/nav';
import NavIcon from '../shared/components/NavIcon';

// How long a just-left field is watched for a tab change before we stop caring.
// Covers the 300ms value debounce plus the background gRPC re-validation.
const MOVE_WATCH_MS = 8000;

// Filter tab key -> its key in the filterCounts badge tally.
const COUNT_KEY_FOR_TAB: Record<FilterTabKey, 'extracted' | 'aiPrefilled' | 'errors' | 'optional'> = {
  extracted: 'extracted',
  ai_prefilled: 'aiPrefilled',
  errors: 'errors',
  optional: 'optional',
};

const ICON_FOR_SECTION = (key: string, label: string): string => {
  const k = (key + ' ' + label).toLowerCase();
  if (k.includes('kopf') || k.includes('header')) return formIcons.headerInformation;
  if (k.includes('ausfuhr') || k.includes('export') || k.includes('exporter')) return formIcons.exporter;
  if (k.includes('empfaenger') || k.includes('empfanger') || k.includes('recipient') || k.includes('buyer') || k.includes('empf')) return formIcons.recipient;
  if (k.includes('anmelder') || k.includes('declarant')) return formIcons.declarant;
  if (k.includes('position') || k.includes('item') || k.includes('line')) return formIcons.lineItems;
  if (k.includes('error')) return formIcons.allErrors;
  return formIcons.headerInformation;
};

// ============================================================================
// AUDIT FIX: Error Boundary for ML Component Failures  
// ============================================================================

interface MLErrorBoundaryProps {
  children: React.ReactNode;
}

interface MLErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class MLErrorBoundary extends React.Component<MLErrorBoundaryProps, MLErrorBoundaryState> {
  constructor(props: MLErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): MLErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 ML Prediction Component Error:', error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: 'warning.light',
          textAlign: 'center'
        }}>
          <Typography variant="caption" color="warning.dark">
            {/* Will be translated in parent component */}
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// AUDIT FIX: Proper TypeScript Interfaces for Type Safety
// ============================================================================

interface AtlasField {
  path: string;
  label: string;
  section: string;
  required: boolean;
  type?: string;
  placeholder?: string;
}

interface FieldCategorization {
  primary: AtlasField[];
  secondary: AtlasField[];
}

interface SectionCompleteness {
  completed: number;
  total: number;
  percentage: number;
}

type FieldStatus = 'complete' | 'missing' | 'verify' | 'optional';

interface MLPredictionsResult {
  header?: Record<string, { value: string; confidence: number }>;
  position?: Record<string, { value: string; confidence: number }>;
  positions?: Array<Record<string, { value: string; confidence: number }>>;
}

interface AtlasStructuredFormProps {
  extractedData: Record<string, any>;
  onFieldChange: (fieldPath: string, value: string) => void;
  fieldValidationErrors?: Record<string, string>;
  onClearFieldError?: (fieldPath: string) => void;
  getFieldError?: (fieldPath: string) => string | undefined;
  /** When true, uses 3-column layout for form fields (for wider viewport) */
  useWideLayout?: boolean;
  mlPredictions?: MLPredictionsResult | null;
  /** Historical suggestion values keyed by field path (e.g. "kopf.empfanger.name" -> "ACME GmbH") */
  historySuggestions?: Record<string, string> | null;
  /** Callback when an ML prediction is shown for a field (for correction tracking) */
  onPredictionShown?: (fieldPath: string, value: string) => void;
  /** Set of field paths whose values were auto-filled by the AI HS code classifier */
  aiClassifiedFields?: Set<string>;
  /** Paths whose current value was applied from an ML prediction (recorded provenance). */
  aiAppliedFields?: Set<string>;
  /** Deep clone of extractedData captured right after OCR/merge, before profile/ML injection.
   *  When provided, drives the Extracted tab to show only fields with true document provenance. */
  documentSnapshot?: Record<string, any> | null;
  /** Called when the user manually applies an ML prediction chip, so the parent can record provenance. */
  onPredictionApplied?: (fieldPath: string) => void;
  /** Per-field merge provenance (multi-document mode). Absent => no provenance UI. */
  fieldSources?: Record<string, { sourceName: string; confidence: number; alternatives: Array<{ value: any; sourceName: string }> }> | null;
  /** Optional actions rendered right-aligned in the filter-tabs row (e.g. Load from History / Apply All). */
  headerActions?: React.ReactNode;
}

interface SectionConfig {
  name: string;
  titleKey: string;
  descriptionKey: string;
}

// Stable identity for the default empty provenance set so memoized callbacks
// don't see a new Set() each render.
const EMPTY_STRING_SET: Set<string> = new Set();

const AtlasStructuredForm: React.FC<AtlasStructuredFormProps> = ({
  extractedData,
  onFieldChange,
  fieldValidationErrors = {},
  onClearFieldError,
  getFieldError: externalGetFieldError,
  useWideLayout = false,
  mlPredictions,
  historySuggestions,
  onPredictionShown,
  aiClassifiedFields,
  aiAppliedFields = EMPTY_STRING_SET,
  fieldSources = null,
  headerActions,
  documentSnapshot = null,
  onPredictionApplied,
}) => {
  const theme = useTheme();
  const { tString } = useTranslation();
  const currentLanguage = useLanguageStore((state) => state.currentLanguage);

  // Internal fuzzy matching for gRPC validation errors
  const getFieldErrorInternal = useCallback((fieldPath: string): string | undefined => {
    // A message of nothing but whitespace is NOT an error. It is truthy, so it
    // painted the field red and counted towards the badge, and then rendered as
    // blank text — a red box with no reason given, which the user cannot act
    // on. Seen on "Document Type (Item 1)". Normalise here, once, rather than
    // at each of the dozen call sites.
    const nonBlank = (value: string | undefined): string | undefined =>
      value && String(value).trim() ? value : undefined;

    const lookup = (): string | undefined => {
    // Use external function if provided (from DocumentProcessing with full fuzzy matching)
    if (externalGetFieldError) {
      return externalGetFieldError(fieldPath);
    }

    if (!fieldPath || Object.keys(fieldValidationErrors).length === 0) return undefined;

    // Helper: extract position index from path like "position[1].field" -> "1"
    const getPositionIndex = (path: string): string | null => {
      const m = path.match(/^position\[(\d+)\]/);
      return m ? m[1] : null;
    };

    const fieldPosIdx = getPositionIndex(fieldPath);
    const isPositionField = fieldPosIdx !== null;

    // 1. Try exact match first
    if (fieldValidationErrors[fieldPath]) {
      return fieldValidationErrors[fieldPath];
    }

    // 2. Try matching without 'kopf.' prefix (NOT position — index must match)
    if (!isPositionField) {
      const simplifiedPath = fieldPath.replace(/^kopf\./, '');
      for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
        if (getPositionIndex(errorPath) !== null) continue;
        const simplifiedErrorPath = errorPath.replace(/^kopf\./, '');
        if (simplifiedErrorPath === simplifiedPath) {
          return errorMsg;
        }
      }
    }

    // 3. Try parent path matching (respect position indices)
    for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
      if (isPositionField) {
        const errorPosIdx = getPositionIndex(errorPath);
        if (errorPosIdx !== null && errorPosIdx !== fieldPosIdx) continue;
      }
      // A bare container root ("kopf", "position[0]") is NOT a parent to
      // inherit from. It is a whole-object error, and letting it match every
      // child broadcasts one problem onto every field: ATLAS answered a single
      // "required property 'gesamtRohmasse' not found" against expdat.0.kopf
      // and the form rendered 76 identical errors, one per header field, with
      // no way to tell which field was actually at fault. Such errors are
      // surfaced once in the unattached-errors list instead.
      const isContainerRoot = !errorPath.includes('.') || /^position\[\d+\]$/.test(errorPath);
      if (!isContainerRoot) {
        if (fieldPath.startsWith(errorPath + '.')) {
          return errorMsg;
        }
        if (errorPath.startsWith(fieldPath + '.')) {
          return errorMsg;
        }
      }
    }

    // 4. Try matching the last part for non-position fields
    if (!isPositionField) {
      const lastPart = fieldPath.split('.').pop() || '';
      for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
        if (getPositionIndex(errorPath) !== null) continue;
        const errorLastPart = errorPath.split('.').pop() || '';
        if (lastPart === errorLastPart) {
          const fieldContext = fieldPath.includes('empfanger') ? 'empfanger' :
                              fieldPath.includes('ausfuhrer') ? 'ausfuhrer' :
                              fieldPath.includes('anmelder') ? 'anmelder' : '';
          const errorContext = errorPath.includes('empfanger') ? 'empfanger' :
                              errorPath.includes('ausfuhrer') ? 'ausfuhrer' :
                              errorPath.includes('anmelder') ? 'anmelder' : '';
          if (fieldContext && fieldContext === errorContext) {
            return errorMsg;
          }
        }
      }
    }

    // 5. Try partial match (respect position indices)
    for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
      if (isPositionField) {
        const errorPosIdx = getPositionIndex(errorPath);
        if (errorPosIdx !== null && errorPosIdx !== fieldPosIdx) continue;
      }
      if (errorPath.endsWith(fieldPath) || fieldPath.endsWith(errorPath)) {
        return errorMsg;
      }
    }

    // 6. Try matching with 'kopf.' prefix added/removed (not for position fields)
    if (!isPositionField) {
      const withKopf = fieldPath.startsWith('kopf.') ? fieldPath : `kopf.${fieldPath}`;
      const withoutKopf = fieldPath.startsWith('kopf.') ? fieldPath.replace(/^kopf\./, '') : fieldPath;

      if (fieldValidationErrors[withKopf]) {
        return fieldValidationErrors[withKopf];
      }
      if (fieldValidationErrors[withoutKopf]) {
        return fieldValidationErrors[withoutKopf];
      }
    }

    // 7. Try matching position fields with dot notation (same index only)
    if (isPositionField) {
      const dotVariant = fieldPath.replace(/position\[(\d+)\]/, 'position.$1');
      if (fieldValidationErrors[dotVariant]) {
        return fieldValidationErrors[dotVariant];
      }
    }

    return undefined;
    };

    return nonBlank(lookup());
  }, [fieldValidationErrors, externalGetFieldError]);
  
  // ============================================================================
  // AUDIT FIX: Loading States for Better UX
  // ============================================================================
  const [isFieldUpdating, setIsFieldUpdating] = useState<Record<string, boolean>>({});

  // Local field values for immediate display - only syncs to parent on blur
  const [localFieldValues, setLocalFieldValues] = useState<Record<string, string>>({});

  // Paths the user has manually edited to a non-empty value. Used to move an AI-prefilled field
  // out of the AI Prefilled tab and into Extracted once the user takes ownership of the value.
  // Populated ONLY from real input onChange (debouncedFieldChange) — never from the auto-apply or
  // defaults effects — so programmatic prefill is not misclassified as a user edit.
  const [userEditedFields, setUserEditedFields] = useState<Set<string>>(new Set());

  // Document identity (LRN) of the last extracted data we saw — used to reset
  // user-edited provenance when a genuinely new document is loaded so tab
  // placement doesn't carry over from the previous document.
  const lastLrnRef = useRef<string>('');

  // State for codelists
  const [codelists, setCodelists] = useState<Record<string, any>>({});
  const [codelistsLoading, setCodelistsLoading] = useState<Record<string, boolean>>({});
  const [codelistsInitialized, setCodelistsInitialized] = useState(false);

  // Generate ATLAS fields dynamically based on extracted data
  // This ensures all position items (line items) are rendered
  const atlasFields = useMemo(() => {
    return getATLASFields(extractedData);
  }, [extractedData]);
  
  // Shared codelist loader — called on mount and on external refresh events
  const loadCodelists = useCallback(async (forceReload = false) => {
    console.log(`AtlasStructuredForm: ${forceReload ? 'Force-reloading' : 'Loading'} codelists...`);

    try {
      if (forceReload) {
        // forceRefresh() already cleared localStorage & in-memory cache in the service;
        // just re-initialize so we get fresh data from backend
        await codelistService.initialize();
      } else {
        await codelistService.initialize();
      }
    } catch (error) {
      console.error('AtlasStructuredForm: Failed to initialize codelist service:', error);
    }

    const fieldPaths = atlasFields.map(f => f.path);
    const codelistIds = new Set<string>();

    for (const fieldPath of fieldPaths) {
      const codelistId = codelistService.getCodelistIdForField(fieldPath);
      if (codelistId) codelistIds.add(codelistId);
    }

    if (codelistIds.size === 0) {
      setCodelistsInitialized(true);
      return;
    }

    const loadingState: Record<string, boolean> = {};
    Array.from(codelistIds).forEach(id => { loadingState[id] = true; });
    setCodelistsLoading(loadingState);

    try {
      const fetchedCodelists = await codelistService.getMultipleCodelists(Array.from(codelistIds));
      console.log('AtlasStructuredForm: Loaded', Object.keys(fetchedCodelists).length, 'codelists. IDs:', Object.keys(fetchedCodelists).join(', '));

      setCodelists(fetchedCodelists);
      setCodelistsInitialized(true);
    } catch (error) {
      console.error('Error loading codelists:', error);
      setCodelistsInitialized(true);
    } finally {
      setCodelistsLoading({});
    }
  }, [atlasFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch codelists on mount / when fields change
  useEffect(() => {
    let cancelled = false;
    loadCodelists(false).then(() => { if (cancelled) return; });
    return () => { cancelled = true; };
  }, [atlasFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch codelists when the user triggers a backend refresh from Profile / Admin pages
  useEffect(() => {
    const handleRefreshEvent = () => {
      console.log('AtlasStructuredForm: Received codelist:refreshed event — reloading...');
      loadCodelists(true);
    };
    window.addEventListener('codelist:refreshed', handleRefreshEvent);
    return () => window.removeEventListener('codelist:refreshed', handleRefreshEvent);
  }, [loadCodelists]);

  // Reset user-edited provenance when a genuinely new document is loaded (LRN
  // changes), so a field's tab placement doesn't carry over from a prior document.
  useEffect(() => {
    const currentLrn = (extractedData as any)?.kopf?.lrn || '';
    if (currentLrn && currentLrn !== lastLrnRef.current) {
      lastLrnRef.current = currentLrn;
      setUserEditedFields(new Set());
    }
  }, [extractedData]);

  // ============================================================================
  // AUDIT FIX: Helper Functions with Proper Types
  // ============================================================================

  // Memoized nested value getter to prevent repeated calculations
  const memoizedGetNestedValue = useCallback((obj: any, path: string): any => {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    return keys.reduce((acc, key) => {
      if (acc === null || acc === undefined) return undefined;
      return acc[key];
    }, obj);
  }, []);
  
  // Evaluate dynamic field rules based on current form data (DEXPDF_10 spec)
  const fieldRuleStatuses = useMemo((): Record<string, RuleFieldStatus> => {
    if (!extractedData) return {};
    return evaluateFieldRules(extractedData);
  }, [extractedData]);

  // Field categorization function with memoized access + dynamic rules
  const categorizedFields = useMemo((): FieldCategorization => {
    const primary: AtlasField[] = [];
    const secondary: AtlasField[] = [];

    atlasFields.forEach(field => {
      const value = memoizedGetNestedValue(extractedData, field.path);
      const hasValue = value !== null && value !== undefined && value !== '';
      const dynamicStatus = fieldRuleStatuses[field.path];
      const isRequired = dynamicStatus === 'required' || (!dynamicStatus && field.required);
      const isHidden = dynamicStatus === 'hidden';
      const hasError = !!getFieldErrorInternal(field.path);

      // Skip hidden fields entirely
      if (isHidden) return;

      if (isRequired || hasValue || hasError) {
        primary.push(field);
      } else {
        secondary.push(field);
      }
    });

    console.log('📊 Field categorization:', {
      total: atlasFields.length,
      primary: primary.length,
      secondary: secondary.length
    });

    return { primary, secondary };
  }, [atlasFields, extractedData, fieldValidationErrors, fieldRuleStatuses]);

  const sections: SectionConfig[] = [
    {
      name: 'kopf',
      titleKey: 'documentProcessing.atlasForm.sections.header',
      descriptionKey: 'documentProcessing.atlasForm.sections.headerDesc',
    },
    {
      name: 'ausfuhrer',
      titleKey: 'documentProcessing.atlasForm.sections.exporter',
      descriptionKey: 'documentProcessing.atlasForm.sections.exporterDesc',
    },
    {
      name: 'empfaenger',
      titleKey: 'documentProcessing.atlasForm.sections.recipient',
      descriptionKey: 'documentProcessing.atlasForm.sections.recipientDesc',
    },
    {
      name: 'anmelder',
      titleKey: 'documentProcessing.atlasForm.sections.declarant',
      descriptionKey: 'documentProcessing.atlasForm.sections.declarantDesc',
    },
    // Note: lieferung and sendung sections removed - fields are now directly in kopf per BEO ATLAS spec
    {
      name: 'position',
      titleKey: 'documentProcessing.atlasForm.sections.lineItems',
      descriptionKey: 'documentProcessing.atlasForm.sections.lineItemsDesc',
    },
  ];
  // Accordion expanded state — all closed by default
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  // Refs for scroll-to-section from summary panel
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Validation summary panel visibility
  const [showSummaryPanel, setShowSummaryPanel] = useState(true);

  // Section navigation: which section is selected in the left nav
  const [selectedSection, setSelectedSection] = useState<string>('kopf');
  // Vertical offset (within the two-panel container) of the connector arrow that
  // links the active nav item to the form panel — set to the active item's center.
  const [connectorTop, setConnectorTop] = useState<number | null>(null);

  // Align the connector arrow with the vertical center of the active nav item.
  // Recomputed on selection change and resize.
  useLayoutEffect(() => {
    const updateConnector = () => {
      const itemEl = sectionRefs.current[selectedSection];
      const containerEl = formContainerRef.current;
      if (!itemEl || !containerEl) {
        setConnectorTop(null);
        return;
      }
      const itemRect = itemEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();
      setConnectorTop(itemRect.top - containerRect.top + itemRect.height / 2);
    };
    updateConnector();
    window.addEventListener('resize', updateConnector);
    return () => window.removeEventListener('resize', updateConnector);
  }, [selectedSection]);
  // Filter tab for the form area: ai_prefilled | errors | optional
  // 'optional' is the internal key for fields with no extraction, no prediction,
  // and no error. The UI label is "Other Fields" — kept to match existing helpers
  // like getOptionalFieldsForSection.
  const [activeFilter, setActiveFilter] = useState<'extracted' | 'ai_prefilled' | 'errors' | 'optional'>('extracted');

  // Additional/optional fields collapsed by default — user can expand per section
  const [showSecondaryFields, setShowSecondaryFields] = useState<Record<string, boolean>>({});

  // State for controlling which line items are expanded/collapsed
  const [expandedLineItems, setExpandedLineItems] = useState<Record<string, boolean>>({ '0': true });

  // ============================================================================
  // AUTO-EXPAND SECTIONS WHEN GRPC VALIDATION ERRORS ARE PRESENT
  // ============================================================================
  useEffect(() => {
    if (!fieldValidationErrors || Object.keys(fieldValidationErrors).length === 0) {
      return;
    }

    // Helper function to determine section from field path
    const getSectionFromFieldPath = (fieldPath: string): string => {
      const path = fieldPath.toLowerCase();
      if (path.includes('position')) return 'position';
      if (path.includes('ausfuhrer') || path.includes('exporter')) return 'ausfuhrer';
      if (path.includes('empfanger') || path.includes('empfaenger') || path.includes('recipient')) return 'empfaenger';
      if (path.includes('anmelder') || path.includes('declarant')) return 'anmelder';
      if (path.includes('vertreter') || path.includes('representative')) return 'anmelder';
      if (path.includes('subunternehmer') || path.includes('subcontractor')) return 'anmelder';
      return 'kopf';
    };

    // Collect sections and secondary fields that have errors
    const sectionsWithErrors = new Set<string>();
    const secondaryFieldsWithErrors = new Set<string>();

    for (const errorPath of Object.keys(fieldValidationErrors)) {
      const section = getSectionFromFieldPath(errorPath);
      sectionsWithErrors.add(section);

      const isSecondaryField = categorizedFields.secondary.some(field => {
        const normalizedErrorPath = errorPath.toLowerCase().replace(/\[|\]|\./g, '');
        const normalizedFieldPath = field.path.toLowerCase().replace(/\[|\]|\./g, '');
        return normalizedErrorPath.includes(normalizedFieldPath) ||
               normalizedFieldPath.includes(normalizedErrorPath) ||
               errorPath.endsWith(field.path) ||
               field.path.endsWith(errorPath);
      });
      if (isSecondaryField) secondaryFieldsWithErrors.add(section);
    }

    // Auto-expand every section that has errors
    if (sectionsWithErrors.size > 0) {
      setExpandedSections(prev => {
        const updated = { ...prev };
        sectionsWithErrors.forEach(s => { updated[s] = true; });
        return updated;
      });
    }

    // Expand secondary fields accordions that have errors
    if (secondaryFieldsWithErrors.size > 0) {
      setShowSecondaryFields(prev => {
        const newShowSecondary = { ...prev };
        Array.from(secondaryFieldsWithErrors).forEach(section => {
          if (!newShowSecondary[section]) {
            newShowSecondary[section] = true;
          }
        });
        return newShowSecondary;
      });
    }

    // Auto-expand line items that have any validation error (error-first visibility)
    const lineItemsWithErrors = new Set<string>();
    for (const errorPath of Object.keys(fieldValidationErrors)) {
      const posMatch = errorPath.match(/^position\[(\d+)\]/);
      if (posMatch) lineItemsWithErrors.add(posMatch[1]);
    }
    if (lineItemsWithErrors.size > 0) {
      setExpandedLineItems(prev => {
        const updated = { ...prev };
        lineItemsWithErrors.forEach(idx => { updated[idx] = true; });
        return updated;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValidationErrors]);

  // ============================================================================
  // AUTO-SWITCH TO ERRORS TAB — fires once per document, never during editing
  //
  // Problem: every field blur triggers gRPC re-validation → fieldValidationErrors
  // changes → naively auto-switching on every change interrupts the user.
  // Secondary problem: onClearFieldError optimistically empties the last error, so
  // a simple "was empty → now has errors" gate resets too readily.
  //
  // Solution: once auto-switched for a given document (identified by LRN), record
  // that LRN and never auto-switch again for it, regardless of how errors oscillate.
  // A new document (different LRN) resets the gate so the first-errors switch fires
  // once more.
  // ============================================================================
  const autoSwitchedForLrnRef = useRef<string>('__never__');

  useEffect(() => {
    const hasErrors = !!fieldValidationErrors && Object.keys(fieldValidationErrors).length > 0;
    if (!hasErrors) return; // errors cleared — nothing to do, do NOT reset the gate

    // Identify the current document by LRN (stable across field edits)
    const currentLrn = String((extractedData as any)?.kopf?.lrn || '');
    // Use a sentinel when LRN is absent so the first-time switch still fires
    const docKey = currentLrn || '__unknown__';

    if (docKey !== autoSwitchedForLrnRef.current) {
      // New document — auto-switch once and record it
      autoSwitchedForLrnRef.current = docKey;
      setSelectedSection('all_errors');
      setActiveFilter('errors');
    }
    // Same document → user is actively working; leave them where they are
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValidationErrors]);

  // ============================================================================
  // AUDIT FIX: Memory Leak Cleanup & Performance Optimization
  // ============================================================================
  
  // Refs to track cleanup needs
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const formContainerRef = useRef<HTMLDivElement>(null);

  // Scroll handler for navigation button
  const scrollToBottom = useCallback(() => {
    const container = formContainerRef.current;
    if (container) {
      // Find the last accordion (not the scroll button)
      const accordions = container.querySelectorAll('.MuiAccordion-root');
      if (accordions.length > 0) {
        const lastAccordion = accordions[accordions.length - 1];
        lastAccordion.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else {
        // Fallback: scroll to bottom of page
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      // Reset ML coordination so a stuck applying-flag can't outlive this form.
      setApplyingPredictions(false);
      clearMlBlackout();
      console.log('🧹 AtlasStructuredForm: Cleaned up memory leaks');
    };
  }, []);
  
  // Debounced field change handler to improve performance with loading state.
  const debouncedFieldChange = useCallback((fieldPath: string, value: string, isAIApplied = false) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    const isEmpty = value === '' || value === null || value === undefined;

    // Track user-edited fields for provenance (AI Prefilled -> Extracted on edit).
    // AI-applied values (Apply button) should NOT move fields out of AI Prefilled tab.
    // Use a functional update that returns the SAME Set reference when nothing changes,
    // so we don't trigger needless re-renders on every keystroke.
    if (!isAIApplied) setUserEditedFields((prev: Set<string>) => {
      if (isEmpty) {
        if (!prev.has(fieldPath)) return prev;
        const next = new Set(prev);
        next.delete(fieldPath);
        return next;
      }
      if (prev.has(fieldPath)) return prev;
      const next = new Set(prev);
      next.add(fieldPath);
      return next;
    });

    // Show loading state
    setIsFieldUpdating(prev => ({ ...prev, [fieldPath]: true }));

    debounceTimer.current = setTimeout(() => {
      if (isMountedRef.current) {
        onFieldChange(fieldPath, value);
        setIsFieldUpdating(prev => ({ ...prev, [fieldPath]: false }));
      }
    }, 300); // 300ms debounce
  }, [onFieldChange]);
  
  // ============================================================================
  // AUDIT FIX: Input Sanitization for Security
  // ============================================================================
  
  const sanitizeInput = useCallback((value: string): string => {
    if (typeof value !== 'string') return String(value || '');
    
    // Remove potentially dangerous HTML/JS characters
    return value
      .replace(/[<>]/g, '') // Remove < and >
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
      .replace(/&lt;/gi, '') // Remove HTML encoded <
      .replace(/&gt;/gi, ''); // Remove HTML encoded >
  }, []);

  // Legacy function - now using memoized version above
  const getNestedValue = memoizedGetNestedValue;

  // Helper function to translate field labels
  const translateFieldLabel = (englishLabel: string): string => {
    // Remove item suffix like " (Item 1)" for matching, then add it back
    const itemMatch = englishLabel.match(/^(.+?)(\s*\(Item \d+\))$/);
    const baseLabel = itemMatch ? itemMatch[1] : englishLabel;
    const itemSuffix = itemMatch ? itemMatch[2] : '';

    // Create a mapping of English labels to translation keys
    const labelMap: Record<string, string> = {
      // Kopf (Header) fields
      'Invoice Number (LRN)': tString('atlasFields.invoiceNumber'),
      'Type of Registration': tString('atlasFields.declarationType'),
      'Declaration Type': tString('atlasFields.declarationType'),
      'Export Declaration Type': tString('atlasFields.exportDeclarationType'),
      'Participant Constellation': tString('atlasFields.participantConstellation'),
      'Declaration Timestamp': tString('atlasFields.declarationTimestamp'),
      'Security Indicator': tString('atlasFields.securityIndicator'),
      'Customs Office of Presentation': tString('atlasFields.customsOfficePresentation'),
      'Export Customs Office': tString('atlasFields.exportCustomsOffice'),
      'Total Gross Mass': tString('atlasFields.totalGrossMass'),
      'Country of Destination': tString('atlasFields.countryOfDestination'),
      'Incoterm Code': tString('atlasFields.incotermCode'),
      'Total Invoice Amount': tString('atlasFields.totalInvoiceAmount'),
      'Invoice Currency': tString('atlasFields.invoiceCurrency'),
      'Invoice Date': tString('atlasFields.invoiceDate'),
      'Export Country': tString('atlasFields.exportCountry'),
      'Destination Country': tString('atlasFields.destinationCountry'),
      'Delivery Place': tString('atlasFields.deliveryPlace'),
      'Domestic Transport Mode': tString('atlasFields.domesticTransportMode'),
      'Border Transport Mode': tString('atlasFields.borderTransportMode'),
      'Total Gross Weight (kg)': tString('atlasFields.totalGrossWeight'),
      'Container Indicator': tString('atlasFields.containerIndicator'),
      'Number of Positions': tString('atlasFields.numberOfPositions'),
      'Reference Number': tString('atlasFields.referenceNumber'),
      'EORI Branch Number': tString('atlasFields.eoriBranchNumber'),
      'Intended Exit Customs Office': tString('atlasFields.intendedExitCustomsOffice'),
      'Loading Place Code': tString('atlasFields.loadingPlaceCode'),
      'Border Transport Mode Type': tString('atlasFields.borderTransportModeType'),
      'Border Transport Nationality': tString('atlasFields.borderTransportNationality'),

      // Exporter fields
      'Exporter Name': tString('atlasFields.exporterName'),
      'Exporter Street': tString('atlasFields.exporterStreet'),
      'Exporter Postal Code': tString('atlasFields.exporterPostalCode'),
      'Exporter City': tString('atlasFields.exporterCity'),
      'Exporter Country': tString('atlasFields.exporterCountry'),
      'Exporter EORI/TIN': tString('atlasFields.exporterEORI'),

      // Recipient fields
      'Recipient Name': tString('atlasFields.recipientName'),
      'Recipient Street': tString('atlasFields.recipientStreet'),
      'Recipient Postal Code': tString('atlasFields.recipientPostalCode'),
      'Recipient City': tString('atlasFields.recipientCity'),
      'Recipient Country': tString('atlasFields.recipientCountry'),
      'Recipient EORI': tString('atlasFields.recipientEORI'),
      'Recipient Identification Type': tString('atlasFields.recipientIdentificationType'),

      // Anmelder (Declarant) fields
      'Declarant Name': tString('atlasFields.declarantName'),
      'Declarant Street': tString('atlasFields.declarantStreet'),
      'Declarant Postal Code': tString('atlasFields.declarantPostalCode'),
      'Declarant City': tString('atlasFields.declarantCity'),
      'Declarant Country': tString('atlasFields.declarantCountry'),
      'Declarant EORI': tString('atlasFields.declarantEORI'),
      'Declarant EORI/TIN': tString('atlasFields.declarantEORI'),
      'Declarant Authorized Name': tString('atlasFields.declarantAuthorizedName'),
      'Declarant Contact Person': tString('atlasFields.declarantContactPerson'),
      'Declarant Phone': tString('atlasFields.declarantPhone'),
      'Declarant Branch Number': tString('atlasFields.declarantBranchNumber'),
      'Declarant Identification Type': tString('atlasFields.declarantIdentificationType'),

      // Position fields
      'Position Number': tString('atlasFields.positionNumber'),
      'Requested Procedure': tString('atlasFields.requestedProcedure'),
      'Previous Procedure': tString('atlasFields.previousProcedure'),
      'Country of Origin': tString('atlasFields.countryOfOrigin'),
      'Product Description': tString('atlasFields.productDescription'),
      'HS/CN Code': tString('atlasFields.hsCode'),
      'HS Code': tString('atlasFields.hsCode'),
      'Gross Weight (kg)': tString('atlasFields.grossWeight'),
      'Net Weight (kg)': tString('atlasFields.netWeight'),
      'Net Weight': tString('atlasFields.netWeight'),
      'Quantity': tString('atlasFields.quantity'),
      'Unit': tString('atlasFields.unit'),
      'Unit Value': tString('atlasFields.unitValue'),
      'Total Value': tString('atlasFields.totalValue'),
      'Currency': tString('atlasFields.currency'),
      'User ID (System)': tString('atlasFields.userId'),

      // Additional fields - Header
      'Type of Transaction': tString('atlasFields.typeOfTransaction'),
      'Nature of Transaction': tString('atlasFields.natureOfTransaction'),
      'Customs Office Number': tString('atlasFields.customsOfficeNumber'),
      'Special Circumstances': tString('atlasFields.specialCircumstances'),
      'Additional Location Identifier': tString('atlasFields.additionalLocationIdentifier'),
      'Supplementary Export Customs Office': tString('atlasFields.supplementaryExportCustomsOffice'),

      // Timestamps / dates
      'Relevant Date': tString('atlasFields.relevantDate'),
      'Date of Exit': tString('atlasFields.dateOfExit'),
      'Presentation Timestamp': tString('atlasFields.presentationTimestamp'),
      'End of Loading Timestamp': tString('atlasFields.endOfLoadingTimestamp'),

      // Delivery / transport
      'Delivery Terms Text': tString('atlasFields.deliveryTermsText'),
      'Transport Cost Payment Method': tString('atlasFields.transportCostPaymentMethod'),
      'Transport Payment Method': tString('atlasFields.transportCostPaymentMethod'),
      'Transit Countries': tString('atlasFields.transitCountries'),

      // Location (Warenort) fields
      'Location Type': tString('atlasFields.locationType'),
      'Location Determination Type': tString('atlasFields.locationDeterminationType'),
      'Location Authorization Number': tString('atlasFields.locationAuthorizationNumber'),

      // Departure transport fields
      'Transport ID Type at Departure': tString('atlasFields.transportIdTypeAtDeparture'),
      'Vehicle Registration at Departure': tString('atlasFields.vehicleRegistrationAtDeparture'),
      'Vehicle Nationality at Departure': tString('atlasFields.vehicleNationalityAtDeparture'),

      // Additional fields - Border Transport
      'Border Vehicle Nationality': tString('atlasFields.borderVehicleNationality'),
      'Border Vehicle Registration': tString('atlasFields.borderVehicleRegistration'),

      // Recipient extra
      'Recipient TIN/EORI': tString('atlasFields.recipientTINEORI'),
      'Recipient EORI/TIN': tString('atlasFields.recipientEORI'),
      'Recipient Branch Number': tString('atlasFields.recipientBranchNumber'),

      // Position packaging / special fields
      'Package Type': tString('atlasFields.packageType'),
      'Number of Packages': tString('atlasFields.numberOfPackagesItem'),
      'Shipping Marks': tString('atlasFields.shippingMarks'),
      'Quantity in Special Unit': tString('atlasFields.quantityInSpecialUnit'),
      'Statistical Value': tString('atlasFields.statisticalValue'),

      // Additional fields - Representative
      'Representative EORI/TIN': tString('atlasFields.representativeTIN'),
      'Representative TIN': tString('atlasFields.representativeTIN'),
      'Representative Name': tString('atlasFields.representativeName'),
      'Representative Phone': tString('atlasFields.representativePhone'),
      'Representative Branch Number': tString('atlasFields.representativeBranchNumber'),
      'Representative Contact Person': tString('atlasFields.representativeContactPerson'),

      // Additional fields - Subcontractor
      'Subcontractor Branch Number': tString('atlasFields.subcontractorBranchNumber'),

      // Authorization (Bewilligung)
      'Authorization Type': tString('atlasFields.authorizationType'),
      'Authorization Reference Number': tString('atlasFields.authorizationReference'),

      // System / header fields
      'Version Number': tString('atlasFields.versionNumber'),
      'Creation Date': tString('atlasFields.creationDate'),
      'Procedure Code': tString('atlasFields.procedureCode'),
      'Procedure': tString('atlasFields.procedureCode'),
      'User ID': tString('atlasFields.userId'),

      // Sender
      'Sender Name': tString('atlasFields.senderName'),
      'Sender Street': tString('atlasFields.senderStreet'),
      'Sender Postal Code': tString('atlasFields.senderPostalCode'),
      'Sender City': tString('atlasFields.senderCity'),
      'Sender Country': tString('atlasFields.senderCountry'),
      'Sender EORI/TIN': tString('atlasFields.senderEORI'),

      // External Exporter
      'External Exporter Name': tString('atlasFields.externalExporterName'),
      'External Exporter Street': tString('atlasFields.externalExporterStreet'),
      'External Exporter Postal Code': tString('atlasFields.externalExporterPostalCode'),
      'External Exporter City': tString('atlasFields.externalExporterCity'),
      'External Exporter Country': tString('atlasFields.externalExporterCountry'),
      'External Exporter EORI/TIN': tString('atlasFields.externalExporterEORI'),

      // Additional fields - Position
      'Region of Origin': tString('atlasFields.regionOfOrigin'),
      'Region of Origin/Dispatch': tString('atlasFields.regionOfOrigin'),
    };

    const translated = labelMap[baseLabel];
    if (translated) {
      return translated + itemSuffix;
    }
    return englishLabel;
  };

  const getFieldsForSection = (sectionName: string) => {
    return atlasFields.filter(field => field.section === sectionName);
  };

  // Get primary (important) fields for a section
  const getPrimaryFieldsForSection = (sectionName: string) => {
    return categorizedFields.primary.filter(field => field.section === sectionName);
  };

  // Get secondary (optional) fields for a section  
  const getSecondaryFieldsForSection = (sectionName: string) => {
    return categorizedFields.secondary.filter(field => field.section === sectionName);
  };

  // ============================================================================
  // AUDIT FIX: Enhanced Field Rendering with Security & Accessibility
  // ============================================================================
  
  const renderFieldWithMLPrediction = (field: AtlasField) => {
    // Use memoized function and sanitize input
    const rawValue = memoizedGetNestedValue(extractedData, field.path) || '';
    // Strip malformed ML float artifacts ("0", "0.0") from niederlassungsNummer.
    // "0000" is the valid ATLAS main-branch code and must be preserved.
    const isZeroNiederlassung = field.path.includes('niederlassungsNummer') &&
      ['0', '0.0'].includes(String(rawValue).trim());
    const backendValue = sanitizeInput(isZeroNiederlassung ? '' : String(rawValue));
    // Convert backend value to human-readable display format (FR → France, 20251012 → 2025-10-12)
    const extractedDisplayValue = convertToDisplayValue(field.path, backendValue);
    // Use local value if user is editing, otherwise use extracted display value
    const displayValue = localFieldValues[field.path] !== undefined
      ? localFieldValues[field.path]
      : extractedDisplayValue;
    const status = getFieldStatus(field);
    const translatedLabel = translateFieldLabel(field.label);
    const provenance = fieldSources?.[field.path];
    const provenanceTooltip = provenance
      ? `From ${provenance.sourceName} · ${Math.round(provenance.confidence * 100)}% confidence` +
        (provenance.alternatives.length > 0
          ? ` — also in: ${provenance.alternatives.map((a) => a.sourceName).join(', ')}`
          : '')
      : null;
    const labelWithProvenance = provenanceTooltip ? (
      <Tooltip title={provenanceTooltip} arrow>
        <span>
          {translatedLabel}
          <CallMergeIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom', color: 'info.main' }} />
        </span>
      </Tooltip>
    ) : (
      translatedLabel
    );
    const fieldId = `atlas-field-${field.path.replace(/\./g, '-').replace(/\[/g, '-').replace(/\]/g, '')}`;
    // Use fuzzy matching for gRPC validation errors
    const validationError = getFieldErrorInternal(field.path);
    const hasValidationError = !!validationError;

    // Accessibility attributes
    const dynamicFieldStatus = fieldRuleStatuses[field.path];
    const isDynamicallyRequired = dynamicFieldStatus === 'required' || (!dynamicFieldStatus && field.required);
    const ariaLabel = `${translatedLabel}${isDynamicallyRequired ? ' (required)' : ''} - Status: ${status}`;
    const ariaDescribedBy = hasValidationError ? `${fieldId}-error` : undefined;
    
    // ── Multi-select chips for transit countries ──────────────────────────────
    if (field.path === 'kopf.beforderungsroute.ausgewahlteLander') {
      const countriesCodelist = codelists['C0248'] || null;
      // rawValue may be an array (defaults), comma-joined (user edits), or the
      // model's $-delimited list (ML apply). parseMultiValue handles all three.
      const rawArr: string[] = Array.isArray(rawValue)
        ? rawValue
        : parseMultiValue(rawValue);

      const selectedOptions = rawArr.map((code: string) => {
        const match = countriesCodelist?.values?.find((v: any) => v.code === code);
        return match || { code, description: code };
      });

      const handleChange = (_: any, newValues: any[]) => {
        const codes = newValues.map((v: any) => (typeof v === 'string' ? v : v.code));
        // Store as comma-separated string (backend will convert to array)
        debouncedFieldChange(field.path, codes.join(','));
      };

      return (
        <Grid item xs={12} sm={6} md={6} lg={6} xl={4} key={field.path}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 0.5,
            borderRadius: 1,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}>
            {/* Field input */}
            <Box sx={{ width: '100%' }}>
              <Autocomplete
                multiple
                id={fieldId}
                data-field-path={field.path}
                options={countriesCodelist?.values || []}
                getOptionLabel={(opt: any) =>
                  typeof opt === 'string' ? opt : `${opt.code} - ${opt.description}`
                }
                isOptionEqualToValue={(opt: any, val: any) =>
                  (typeof val === 'string' ? val : val?.code) === opt.code
                }
                value={selectedOptions}
                onChange={handleChange}
                loading={!codelistsInitialized}
                renderTags={(value: any[], getTagProps: any) =>
                  value.map((opt: any, index: number) => (
                    <Chip
                      key={opt.code}
                      label={`${opt.code} - ${opt.description}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      {...getTagProps({ index })}
                      sx={{ maxWidth: 220, fontSize: '0.75rem' }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translatedLabel}
                    required={isDynamicallyRequired}
                    variant="outlined"
                    size="small"
                    error={hasValidationError}
                    helperText={
                      hasValidationError
                        ? validationError
                        : status === 'missing'
                        ? tString('documentProcessing.atlasForm.fieldHelpers.required')
                        : status === 'verify'
                        ? tString('documentProcessing.atlasForm.fieldHelpers.verify')
                        : ''
                    }
                    FormHelperTextProps={{
                      id: hasValidationError ? `${fieldId}-error` : undefined,
                      sx: {
                        color: (hasValidationError || status === 'missing') ? theme.palette.error.main : '#000000 !important',
                        fontWeight: 400,
                      },
                    }}
                    placeholder={status === 'missing' ? tString('atlasFields.selectField', translatedLabel) : ''}
                    aria-label={ariaLabel}
                    aria-describedby={ariaDescribedBy}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {aiClassifiedFields?.has(field.path) && (
                            <InputAdornment position="end" sx={{ mr: -0.5 }}>
                              <Tooltip title={tString('atlasFields.aiGenerated')} placement="top" arrow>
                                <AutoAwesomeIcon sx={{ fontSize: 18, color: 'info.main' }} />
                              </Tooltip>
                            </InputAdornment>
                          )}
                          {!codelistsInitialized && <CircularProgress color="inherit" size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem',
                        bgcolor:
                          status === 'missing'
                            ? `${theme.palette.error.main}08`
                            : status === 'verify'
                            ? `${theme.palette.warning.main}08`
                            : '#fbfbfb',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem', // 14px when resting inside the field
                        fontWeight: 400,
                      },
                      // Floating label: 1rem × MUI's scale(0.75) ≈ 12px on top
                      '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                        fontSize: '1rem',
                      },
                      '& .MuiFormHelperText-root': { fontSize: '0.65rem', marginTop: '2px' },
                    }}
                  />
                )}
                sx={{ width: '100%' }}
              />
            </Box>

            {/* ML prediction / history row — matches other fields */}
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, minHeight: 24, flexWrap: 'wrap' }}>
              <MLErrorBoundary>
                <MLPredictionCell
                  fieldPath={field.path}
                  fieldValue={rawArr.join(',')}
                  extractedData={extractedData}
                  onApplyPrediction={(prediction) => {
                    debouncedFieldChange(field.path, prediction, true);
                    onPredictionApplied?.(field.path);
                    if (onClearFieldError) onClearFieldError(field.path);
                  }}
                  onPredictionShown={onPredictionShown}
                />
              </MLErrorBoundary>

              {historySuggestions?.[field.path] && (() => {
                const histVal = historySuggestions[field.path];
                if (histVal === rawArr.join(',') || histVal === rawValue) return null;
                const label = histVal.length > 20 ? histVal.slice(0, 20) + '…' : histVal;
                return (
                  <HistoryChip
                    label={label}
                    tooltip={tString('atlasFields.fillFromHistory', histVal)}
                    onApply={() => {
                      debouncedFieldChange(field.path, histVal);
                      if (onClearFieldError) onClearFieldError(field.path);
                    }}
                  />
                );
              })()}
            </Box>
          </Box>
        </Grid>
      );
    }
    // ── End multi-select transit countries ────────────────────────────────────

    // Check if field has a codelist
    const codelistId = codelistService.getCodelistIdForField(field.path);
    const codelist = codelistId ? codelists[codelistId] : null;
    // Field is loading if: has a codelistId AND (codelists not initialized OR explicitly loading)
    const isLoadingCodelist = codelistId ? (!codelistsInitialized || codelistsLoading[codelistId]) : false;
    // Should render Autocomplete if: has codelistId (even if still loading - will show loading spinner)
    const shouldUseAutocomplete = !!codelistId;

    // Responsive layout: xs=12 (1 col), sm/md/lg=6 (2 cols), xl=4 (3 cols on extra-large desktop).
    // Single column on narrow viewports, two columns through large screens, three columns only on
    // extra-large desktops so smaller screens stay at a comfortable 2-column layout.

    return (
      <Grid
        item
        xs={12}
        sm={6}
        md={6}
        lg={6}
        xl={4}
        key={field.path}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          p: 0.5,
          borderRadius: 1,
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
          }
        }}>
          {/* Field Section - Full width input */}
          <Box sx={{ width: '100%' }}>
            {(() => {
              const shouldRenderAutocomplete = shouldUseAutocomplete && (codelist || isLoadingCodelist);
              console.log(`🎯 RENDER DECISION for ${field.path}:`, {
                codelistId,
                codelist: !!codelist,
                codelistValues: codelist?.values?.length || 0,
                isLoadingCodelist,
                codelistsInitialized,
                shouldRenderAutocomplete,
              });
              return shouldRenderAutocomplete;
            })() ? (
              // Render Autocomplete for codelist fields
              <Autocomplete
                freeSolo
                autoSelect
                id={fieldId}
                data-field-path={field.path}
                options={codelist?.values || []}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    // Handle when user types a value not in the list
                    const codelistValue = codelist?.values?.find((v: any) => v.code === option);
                    if (codelistValue) {
                      const localizedDesc = getLocalizedDescription(codelistValue.description, currentLanguage, codelistId || undefined);
                      return `${codelistValue.code} - ${localizedDesc}`;
                    }
                    return option;
                  }
                  const localizedDesc = getLocalizedDescription(option.description, currentLanguage, codelistId || undefined);
                  return `${option.code} - ${localizedDesc}`;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') {
                    return option.code === value;
                  }
                  return option.code === value?.code;
                }}
                value={(() => {
                  if (!codelist) return backendValue || '';
                  // Try to find the best match for the extracted value
                  const bestMatch = codelistService.findBestMatch(backendValue, codelist);
                  if (bestMatch) {
                    return bestMatch;
                  }
                  // If no match found, try exact code match
                  const exactMatch = codelist.values?.find((v: any) => v.code === backendValue);
                  if (exactMatch) {
                    return exactMatch;
                  }
                  // Return the raw value if no match (allow free text)
                  return backendValue || '';
                })()}
                onChange={(event, newValue) => {
                  // A codelist field must store the CODE. This Autocomplete
                  // accepts free text, so typing rather than clicking handed
                  // back a plain string and it was saved verbatim: selecting
                  // "Direct trade with/by private consumers" stored that
                  // sentence in kopf.angabenGeschaftsvorgangArt instead of
                  // "12". ATLAS then reported "Required data field" on a field
                  // that plainly had a value, with nothing to indicate why.
                  // Resolve typed text back to its code before saving.
                  const valueToSave = resolveCodelistValue(newValue, codelist?.values);
                  setLocalFieldValues(prev => ({ ...prev, [field.path]: valueToSave }));
                  debouncedFieldChange(field.path, valueToSave);
                  if (hasValidationError && onClearFieldError) {
                    onClearFieldError(field.path);
                  }
                }}
                loading={isLoadingCodelist}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translatedLabel}
                    required={isDynamicallyRequired}
                    error={hasValidationError}
                    helperText={
                      hasValidationError
                        ? validationError
                        : status === 'missing'
                        ? tString('documentProcessing.atlasForm.fieldHelpers.required')
                        : status === 'verify'
                        ? tString('documentProcessing.atlasForm.fieldHelpers.verify')
                        : ''
                    }
                    FormHelperTextProps={{
                      id: hasValidationError ? `${fieldId}-error` : undefined,
                      sx: {
                        color: (hasValidationError || status === 'missing') ? theme.palette.error.main : '#000000 !important',
                        fontWeight: 400,
                      }
                    }}
                    variant="outlined"
                    size="small"
                    placeholder={status === 'missing' ? tString('atlasFields.selectField', translatedLabel) : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCodelist ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem',
                        bgcolor:
                          status === 'missing'
                            ? `${theme.palette.error.main}08`
                            : status === 'verify'
                            ? `${theme.palette.warning.main}08`
                            : '#fbfbfb',
                        '& input': {
                          padding: '10px 10px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem', // 14px when resting inside the field
                        fontWeight: 400,
                      },
                      // Floating label: 1rem × MUI's scale(0.75) ≈ 12px on top
                      '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                        fontSize: '1rem',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.65rem',
                        marginTop: '2px',
                      },
                    }}
                  />
                )}
              />
            ) : (
              // Render regular TextField for non-codelist fields
              <TextField
              id={fieldId}
              data-field-path={field.path}
              fullWidth
              label={labelWithProvenance}
              value={displayValue}
              onChange={(e) => {
                const sanitizedValue = sanitizeInput(e.target.value);
                setLocalFieldValues(prev => ({ ...prev, [field.path]: sanitizedValue }));
                // Clear error immediately as user types
                if (onClearFieldError) {
                  onClearFieldError(field.path);
                }
                // Debounce-sync to parent so value persists without needing blur
                const backendValueToSend = convertToBackendValue(field.path, sanitizedValue);
                debouncedFieldChange(field.path, backendValueToSend);
              }}
              onBlur={(e) => {
                // Sync to parent on blur (focusout) - this triggers predictions
                const sanitizedValue = sanitizeInput(e.target.value);
                // Clear field error when user finishes editing
                if (onClearFieldError) {
                  onClearFieldError(field.path);
                }
                // Convert display value back to backend format (France → FR, 2025-10-12 → 20251012)
                const backendValueToSend = convertToBackendValue(field.path, sanitizedValue);
                if (backendValueToSend !== backendValue) {
                  debouncedFieldChange(field.path, backendValueToSend);
                  
                  // ============================================================================
                  // Weight Auto-Filling: Bidirectional sync between gross and net weight
                  // ============================================================================
                  // Check if this is a weight field and apply bidirectional auto-filling
                  if (field.path.includes('wareRohmasse') || field.path.includes('wareEigenmasse')) {
                    const positionMatch = field.path.match(/position\[(\d+)\]/);
                    if (positionMatch) {
                      const posIndex = positionMatch[1];
                      const grossPath = `position[${posIndex}].ware.vermessung.wareRohmasse`;
                      const netPath = `position[${posIndex}].ware.vermessung.wareEigenmasse`;
                      
                      // If gross weight was entered and net weight is empty, auto-fill net weight
                      if (field.path.includes('wareRohmasse') && backendValueToSend) {
                        const netWeight = memoizedGetNestedValue(extractedData, netPath);
                        if (!netWeight || netWeight === '' || netWeight === 'null') {
                          console.log(`🔄 Auto-filling net weight from gross weight: ${backendValueToSend}`);
                          debouncedFieldChange(netPath, backendValueToSend);
                        }
                      }
                      
                      // If net weight was entered and gross weight is empty, auto-fill gross weight
                      if (field.path.includes('wareEigenmasse') && backendValueToSend) {
                        const grossWeight = memoizedGetNestedValue(extractedData, grossPath);
                        if (!grossWeight || grossWeight === '' || grossWeight === 'null') {
                          console.log(`🔄 Auto-filling gross weight from net weight: ${backendValueToSend}`);
                          debouncedFieldChange(grossPath, backendValueToSend);
                        }
                      }
                    }
                  }
                }
                // Clear local value after syncing
                setLocalFieldValues(prev => {
                  const newValues = { ...prev };
                  delete newValues[field.path];
                  return newValues;
                });
              }}
              // ============================================================================
              // AUDIT FIX: Accessibility Attributes
              // ============================================================================
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              aria-required={isDynamicallyRequired}
              role="textbox"
              tabIndex={0}
              // ============================================================================
              // AUDIT FIX: Enhanced Keyboard Navigation
              // ============================================================================
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  // Move to next field on Enter
                  const allFields = document.querySelectorAll('[data-field-path]');
                  const currentIndex = Array.from(allFields).findIndex(
                    (el) => el.getAttribute('data-field-path') === field.path
                  );
                  const nextField = allFields[currentIndex + 1] as HTMLElement;
                  if (nextField) {
                    nextField.focus();
                  }
                }
                if (e.key === 'Escape') {
                  // Clear field on Escape - update local state and sync to parent
                  setLocalFieldValues(prev => ({ ...prev, [field.path]: '' }));
                  debouncedFieldChange(field.path, '');
                }
              }}
              required={isDynamicallyRequired}
              error={hasValidationError}
              helperText={
                hasValidationError
                  ? validationError
                  : status === 'missing'
                  ? tString('documentProcessing.atlasForm.fieldHelpers.required')
                  : status === 'verify'
                  ? tString('documentProcessing.atlasForm.fieldHelpers.verify')
                  : ''
              }
              FormHelperTextProps={{
                id: hasValidationError ? `${fieldId}-error` : undefined,
                sx: {
                  color: (hasValidationError || status === 'missing')
                    ? theme.palette.error.main
                    : status === 'verify'
                      ? theme.palette.warning.dark
                      : theme.palette.text.secondary,
                  fontWeight: (hasValidationError || status === 'missing') ? 400 : 500,
                }
              }}
              variant="outlined"
              size="small"
              placeholder={status === 'missing' ? tString('atlasFields.enterField', translatedLabel) : ''}
              InputProps={aiClassifiedFields?.has(field.path) ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={tString('atlasFields.aiGenerated')} placement="top" arrow>
                      <AutoAwesomeIcon sx={{ fontSize: 18, color: 'info.main' }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              } : undefined}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8rem',
                  bgcolor:
                    status === 'missing'
                      ? `${theme.palette.error.main}08`
                      : status === 'verify'
                        ? `${theme.palette.warning.main}08`
                        : '#fbfbfb',
                  '& input': {
                    padding: '8px 10px',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem', // 14px when resting inside the field
                  fontWeight: 400,
                  color: status === 'missing'
                    ? theme.palette.error.main
                    : status === 'verify'
                      ? theme.palette.warning.dark
                      : theme.palette.text.primary,
                },
                // Floating label: 1rem × MUI's scale(0.75) ≈ 12px on top
                '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                  fontSize: '1rem',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.65rem',
                  marginTop: '2px',
                },
              }}
            />
            )}
          </Box>
          
          {/* ML Prediction Section - Compact below input */}
          <Box sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 0.5,
            minHeight: 24,
            flexWrap: 'wrap',
          }}>
            <MLErrorBoundary>
              <MLPredictionCell
                fieldPath={field.path}
                fieldValue={backendValue}
                extractedData={extractedData}
                onApplyPrediction={(prediction) => {
                  const sanitizedPrediction = sanitizeInput(prediction);
                  const displayPrediction = convertToDisplayValue(field.path, sanitizedPrediction);
                  setLocalFieldValues(prev => ({ ...prev, [field.path]: displayPrediction }));
                  debouncedFieldChange(field.path, sanitizedPrediction, true);
                  if (onClearFieldError) onClearFieldError(field.path);
                  onPredictionApplied?.(field.path);
                }}
                onPredictionShown={onPredictionShown}
              />
            </MLErrorBoundary>

            {/* History suggestion chip — single owner, shown whenever history differs from the current value */}
            {historySuggestions?.[field.path] && (() => {
              const histVal = historySuggestions[field.path];
              const histDisplayVal = convertToDisplayValue(field.path, histVal);
              if (histDisplayVal === displayValue || histVal === backendValue) return null;
              const chipDisplay = histDisplayVal || histVal;
              const label = chipDisplay.length > 20 ? chipDisplay.slice(0, 20) + '…' : chipDisplay;
              return (
                <HistoryChip
                  label={label}
                  tooltip={tString('atlasFields.fillFromHistory', chipDisplay)}
                  onApply={() => {
                    const displayVal = convertToDisplayValue(field.path, histVal);
                    setLocalFieldValues(prev => ({ ...prev, [field.path]: displayVal }));
                    debouncedFieldChange(field.path, histVal);
                    if (onClearFieldError) onClearFieldError(field.path);
                  }}
                />
              );
            })()}

            {/* Apply this packaging value to every line item.
                ATLAS wants package type, count and shipping marks on EVERY
                position, while a commercial document states packaging once for
                the whole shipment or not at all. On the 27-item reference
                proforma that is 81 values by hand — and 81 of its 99 remaining
                errors. ATLAS's own shared-packaging reference
                (packstuckverweis) does NOT satisfy the validator (measured:
                96 errors versus 18 when the values are repeated), so they
                genuinely have to be written to each position. */}
            {isBulkFillable(field.path) && (() => {
              const positionCount = Array.isArray((extractedData as any)?.position)
                ? (extractedData as any).position.length
                : 0;
              const value = String(localFieldValues[field.path] ?? displayValue ?? '');
              if (!value.trim()) return null;
              const targets = pathsNeedingFill(
                field.path,
                value,
                positionCount,
                (path) => memoizedGetNestedValue(extractedData, path)
              );
              if (targets.length === 0) return null;
              return (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    targets.forEach((path) => {
                      setLocalFieldValues(prev => ({ ...prev, [path]: value }));
                      onFieldChange(path, value);
                      if (onClearFieldError) onClearFieldError(path);
                    });
                  }}
                  sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem', p: '2px 6px', minWidth: 0 }}
                >
                  {`Apply to all ${positionCount} items`}
                </Button>
              );
            })()}
          </Box>
        </Box>
      </Grid>
    );
  };

  const getSectionCompleteness = (sectionName: string) => {
    const fields = getFieldsForSection(sectionName);
    const completed = fields.filter(field => {
      const value = getNestedValue(extractedData, field.path);
      return value && value !== '' && value !== 'null';
    });
    return {
      completed: completed.length,
      total: fields.length,
      percentage: fields.length > 0 ? (completed.length / fields.length) * 100 : 0,
    };
  };

  const getSectionErrorCount = (sectionName: string): number => {
    if (!fieldValidationErrors || Object.keys(fieldValidationErrors).length === 0) return 0;
    const fields = getFieldsForSection(sectionName);
    return fields.filter(f => !!getFieldErrorInternal(f.path)).length;
  };

  const getSectionMLPredictionsCount = (sectionName: string) => {
    if (!mlPredictions) return 0;
    
    const fields = getFieldsForSection(sectionName);
    let count = 0;
    
    // Get the centralized ML field mappings
    const mappings = getMLFieldMapping();
    
    fields.forEach(field => {
      // Convert ATLAS field path to ML field name
      // Field path might be like "kopf.ausfuhrer.tin" or "position[0].wareWarennummerKN8"
      let fieldToCheck = field.path;
      
      // Remove array notation for position fields
      if (fieldToCheck.includes('position[')) {
        fieldToCheck = fieldToCheck.replace(/position\[\d+\]\./, 'position.');
      }
      
      // Check if this field has an ML mapping
      const mlFieldName = mappings.atlasToML[fieldToCheck] || 
                         mappings.atlasToML[fieldToCheck.replace('kopf.', '')] ||
                         mappings.atlasToML[fieldToCheck.replace('position.', '')];
      
      if (mlFieldName) {
        // Check if we have a prediction for this field
        if (fieldToCheck.startsWith('position') && mlPredictions.position) {
          if (mlPredictions.position[mlFieldName]) {
            count++;
          }
        } else if (mlPredictions.header) {
          if (mlPredictions.header[mlFieldName]) {
            count++;
          }
        }
      }
    });
    
    return count;
  };

  const getFieldStatus = (field: any): 'missing' | 'verify' | 'complete' | 'optional' => {
    const value = memoizedGetNestedValue(extractedData, field.path);
    const hasValue = value && value !== '' && value !== 'null';

    // Use dynamic rules engine (DEXPDF_10 specification)
    const dynamicStatus = fieldRuleStatuses[field.path];
    const isRequired = dynamicStatus === 'required' || (!dynamicStatus && field.required);

    if (!hasValue && isRequired) return 'missing';
    if (hasValue) return 'complete';
    return 'optional';
  };

  // Compute total error count across all sections for the summary panel
  const totalErrorCount = sections.reduce((sum, s) => sum + getSectionErrorCount(s.name), 0);

  // ============================================================================
  // FILTER TAB HELPERS - for section nav + filter tabs layout
  // ============================================================================

  // Paths that have any ML prediction (indexed correctly per position line).
  // Used only for the empty-field "pending prediction" preview case.
  const predictedFieldPaths = useMemo(
    () => buildPredictedFieldPaths(mlPredictions, atlasFields, getMLFieldMapping()),
    [mlPredictions, atlasFields]
  );

  // Paths present in the pure document extraction snapshot (before profile/ML injection).
  // When documentSnapshot is null the set is empty and isDocumentSourced stays undefined.
  const documentSourcedPaths = useMemo(
    () => buildDocumentSourcedPaths(documentSnapshot),
    [documentSnapshot]
  );

  // Extracted Fields: fields whose value came from the document (not an applied prediction).
  const getExtractedFieldsForSection = useCallback((sectionName: string): AtlasField[] => {
    const allFields = getFieldsForSection(sectionName);
    return allFields.filter(field => {
      if (getFieldErrorInternal(field.path)) return false;
      const value = memoizedGetNestedValue(extractedData, field.path);
      const hasValue = !(value === null || value === undefined || value === '');
      return resolveFieldTab({
        hasValue,
        isUserEdited: userEditedFields.has(field.path),
        isAiApplied: aiAppliedFields.has(field.path),
        hasPendingPrediction: predictedFieldPaths.has(field.path) && !hasValue,
        isDocumentSourced: documentSnapshot ? documentSourcedPaths.has(field.path) : undefined,
      }) === 'extracted';
    });
  }, [atlasFields, extractedData, fieldValidationErrors, mlPredictions, userEditedFields, aiAppliedFields, predictedFieldPaths, documentSourcedPaths, documentSnapshot, memoizedGetNestedValue, getFieldErrorInternal]);

  // AI Prefilled: fields whose value was applied from a prediction, or empty fields
  // with a pending prediction (preview). No mapping re-derivation here.
  const getAIPrefilledFieldsForSection = useCallback((sectionName: string): AtlasField[] => {
    const allFields = getFieldsForSection(sectionName);
    return allFields.filter(field => {
      if (getFieldErrorInternal(field.path)) return false;
      const value = memoizedGetNestedValue(extractedData, field.path);
      const hasValue = !(value === null || value === undefined || value === '');
      return resolveFieldTab({
        hasValue,
        isUserEdited: userEditedFields.has(field.path),
        isAiApplied: aiAppliedFields.has(field.path),
        hasPendingPrediction: predictedFieldPaths.has(field.path) && !hasValue,
        isDocumentSourced: documentSnapshot ? documentSourcedPaths.has(field.path) : undefined,
      }) === 'ai_prefilled';
    });
  }, [atlasFields, extractedData, mlPredictions, userEditedFields, aiAppliedFields, predictedFieldPaths, documentSourcedPaths, documentSnapshot, memoizedGetNestedValue, getFieldErrorInternal]);

  // Errors: fields with validation errors only
  const getErrorFieldsForSection = useCallback((sectionName: string): AtlasField[] => {
    const allFields = getFieldsForSection(sectionName);
    return allFields.filter(field => !!getFieldErrorInternal(field.path));
  }, [atlasFields, fieldValidationErrors, getFieldErrorInternal]);

  // Other Fields: empty, no error, and no ML prediction (not in Extracted or AI Prefilled)
  // Optional: empty fields with no value and no prediction (resolver === 'none').
  const getOptionalFieldsForSection = useCallback((sectionName: string): AtlasField[] => {
    const allFields = getFieldsForSection(sectionName);
    return allFields.filter(field => {
      if (getFieldErrorInternal(field.path)) return false;
      const value = memoizedGetNestedValue(extractedData, field.path);
      const hasValue = !(value === null || value === undefined || value === '');
      return resolveFieldTab({
        hasValue,
        isUserEdited: userEditedFields.has(field.path),
        isAiApplied: aiAppliedFields.has(field.path),
        hasPendingPrediction: predictedFieldPaths.has(field.path) && !hasValue,
        isDocumentSourced: documentSnapshot ? documentSourcedPaths.has(field.path) : undefined,
      }) === 'none';
    });
  }, [atlasFields, extractedData, fieldValidationErrors, userEditedFields, aiAppliedFields, predictedFieldPaths, documentSourcedPaths, documentSnapshot, memoizedGetNestedValue, getFieldErrorInternal]);

  // Get all error fields across ALL sections (for "All Errors" virtual section)
  const allErrorFields = useMemo((): AtlasField[] => {
    return atlasFields.filter(field => !!getFieldErrorInternal(field.path));
  }, [atlasFields, fieldValidationErrors, getFieldErrorInternal]);

  // ── Errors that match no rendered field ────────────────────────────────────
  //
  // The COUNT comes from fieldValidationErrors; the LIST above is built by
  // filtering the rendered field registry. An error whose path matches no field
  // can therefore never appear in it — and the user is shown "Errors (1)" over
  // "No validation errors. All fields pass validation.", with a toast telling
  // them to fix issues "highlighted in the form" that are highlighted nowhere.
  //
  // MEASURED: "kopf.unterlagetyp" from ATLAS. It aliases to
  // position[0].unterlage[0].art, a field that stops being rendered in the
  // errors view once it is optional — so the alias existed and the error still
  // vanished. Several other paths did the same thing earlier today.
  //
  // An error that cannot be attached to a control is still the user's problem
  // and must be readable. Listing it with its raw path is worse than a labelled
  // field and far better than a number with nothing behind it.
  const orphanErrors = useMemo((): Array<{ path: string; message: string }> => {
    if (!fieldValidationErrors) return [];
    const claimed = new Set(
      atlasFields
        .filter(field => !!getFieldErrorInternal(field.path))
        .map(field => getFieldErrorInternal(field.path))
    );
    return Object.entries(fieldValidationErrors)
      .filter(([, message]) => !!message && !claimed.has(message as string))
      .map(([path, message]) => ({ path, message: String(message) }));
  }, [atlasFields, fieldValidationErrors, getFieldErrorInternal]);

  // ── Frozen error snapshot ──────────────────────────────────────────────────
  // When the user enters the Errors tab, we snapshot the error fields and hold
  // that list stable while they type.  This prevents fields from disappearing
  // mid-edit as each keystroke triggers a background gRPC re-validation.
  // The snapshot is refreshed only when the user explicitly changes tab or
  // section — never on fieldValidationErrors alone.
  // ──────────────────────────────────────────────────────────────────────────
  const [frozenErrorFields, setFrozenErrorFields] = useState<Record<string, AtlasField[]>>({});

  // The snapshot is deliberately not refreshed on fieldValidationErrors, which
  // keeps fields from vanishing under the cursor mid-edit. The cost is that it
  // goes stale in BOTH directions: it keeps showing a field whose error has
  // since cleared, and hides one whose error appeared after the snapshot. The
  // first is why "Document Type (Item 1)" sat in the Errors tab with a red
  // border and no message at all.
  //
  // A union fixes both without giving up the anti-vanish behaviour: frozen
  // fields stay put, and any field with a LIVE error is added. Whether each one
  // still reads as an error is then decided per field by getFieldErrorInternal,
  // which now ignores blank messages.
  const withLiveErrors = useCallback(
    (frozen: AtlasField[] | undefined, live: AtlasField[]): AtlasField[] => {
      if (!frozen) return live;
      const seen = new Set(frozen.map(f => f.path));
      return [...frozen, ...live.filter(f => !seen.has(f.path))];
    },
    [],
  );

  useEffect(() => {
    if (activeFilter === 'errors') {
      if (selectedSection === 'all_errors') {
        setFrozenErrorFields(prev => ({ ...prev, __all__: allErrorFields }));
      } else {
        setFrozenErrorFields(prev => ({
          ...prev,
          [selectedSection]: getErrorFieldsForSection(selectedSection),
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, selectedSection, aiAppliedFields]);
  // fieldValidationErrors is intentionally excluded: it changes on every
  // keystroke, and refreshing then is what made fields vanish mid-edit.
  // aiAppliedFields is included because it moves ONLY when the user applies a
  // prediction (one field, or "Apply All") — never while typing. Applying can
  // create errors on fields that were previously clean, and without this the
  // badge counted them while the frozen card list still showed the pre-apply
  // set: "5 errors" became "7" with only the original five rendered, so the
  // two new ones were impossible to find.

  // ── Sticky placement of the field being edited ─────────────────────────────
  // A field's tab follows its value, so emptying an input to retype it would
  // move the field to another tab on the keystroke that clears it — with the
  // cursor still inside.  The field the user is on is pinned to the tab it is
  // rendered in and released only when the user leaves it (blur) or switches
  // tab/section; the move is then announced with a toast so nobody has to hunt
  // for the field.
  // ──────────────────────────────────────────────────────────────────────────
  const [stickyFieldPaths, setStickyFieldPaths] = useState<Set<string>>(new Set());

  // Fields released from the pin, watched until their placement settles: the
  // edit is synced to extractedData on a debounce and re-validated over gRPC in
  // the background, so the tab a field ends up in is only known a moment later.
  // path -> expiry timestamp (ms).
  const pendingMoveWatchRef = useRef<Map<string, number>>(new Map());

  // Release every pin when the user changes tab or section — the new tab's list
  // is computed from scratch.
  useEffect(() => {
    setStickyFieldPaths(prev => (prev.size === 0 ? prev : new Set()));
    pendingMoveWatchRef.current.clear();
  }, [activeFilter, selectedSection]);

  // The tab a field belongs to, given the state committed so far. Inputs are the
  // same ones the per-tab getters above use, so a field never gets announced as
  // moved to a tab its own filter disagrees with.
  const classifyFieldTab = useCallback((fieldPath: string): FilterTabKey => {
    const value = memoizedGetNestedValue(extractedData, fieldPath);
    const hasValue = !(value === null || value === undefined || value === '');
    return resolveFilterTab({
      hasError: !!getFieldErrorInternal(fieldPath),
      hasValue,
      isUserEdited: userEditedFields.has(fieldPath),
      isAiApplied: aiAppliedFields.has(fieldPath),
      hasPendingPrediction: predictedFieldPaths.has(fieldPath) && !hasValue,
      isDocumentSourced: documentSnapshot ? documentSourcedPaths.has(fieldPath) : undefined,
    });
  }, [extractedData, userEditedFields, aiAppliedFields, predictedFieldPaths, documentSourcedPaths, documentSnapshot, memoizedGetNestedValue, getFieldErrorInternal]);

  const announceFieldMove = useCallback((fieldPath: string, destination: FilterTabKey) => {
    const field = atlasFields.find(f => f.path === fieldPath);
    const fieldLabel = field ? translateFieldLabel(field.label) : fieldPath;
    const tabLabel = tString(FILTER_TAB_LABEL_KEYS[destination]);
    toast.info(
      tString('documentProcessing.atlasForm.fieldMovedToTab', fieldLabel, tabLabel),
      { toastId: `field-moved-${fieldPath}` },
    );
    // translateFieldLabel/tString are re-created every render; keeping them out
    // of the deps keeps this callback (and the settle effect) stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atlasFields, currentLanguage]);

  const handleFieldAreaFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const fieldPath = fieldPathFromEventTarget(event.target);
    if (!fieldPath) return;
    setStickyFieldPaths(prev => (prev.has(fieldPath) ? prev : new Set(prev).add(fieldPath)));
  }, []);

  const handleFieldAreaBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const fieldPath = fieldPathFromEventTarget(event.target);
    if (!fieldPath) return;
    setStickyFieldPaths(prev => {
      if (!prev.has(fieldPath)) return prev;
      const next = new Set(prev);
      next.delete(fieldPath);
      return next;
    });
    if (selectedSection === 'all_errors') return;
    // The edit is still in flight (debounced sync + background re-validation),
    // so where the field lands is decided by the settle effect below.
    pendingMoveWatchRef.current.set(fieldPath, Date.now() + MOVE_WATCH_MS);
  }, [selectedSection]);

  // Announce where a just-left field ended up, once its edit has settled into
  // extractedData / the validation results.  Fields that stay in the active tab
  // are watched until the window expires, so a late gRPC error is reported too.
  useEffect(() => {
    const watched = pendingMoveWatchRef.current;
    if (watched.size === 0) return;
    const now = Date.now();
    watched.forEach((expiresAt, fieldPath) => {
      if (expiresAt < now) {
        watched.delete(fieldPath);
        return;
      }
      const destination = classifyFieldTab(fieldPath);
      if (destination !== activeFilter) {
        watched.delete(fieldPath);
        announceFieldMove(fieldPath, destination);
      }
    });
  }, [extractedData, fieldValidationErrors, userEditedFields, activeFilter, classifyFieldTab, announceFieldMove]);

  // Compute filter counts for the selected section. The badges follow what is
  // actually rendered: a field pinned while the user edits it counts in the tab
  // it is still shown in, not in the tab its current value implies.
  const filterCounts = useMemo(() => {
    if (selectedSection === 'all_errors') {
      return { extracted: 0, aiPrefilled: 0, errors: totalErrorCount, optional: 0 };
    }
    const counts = {
      extracted: getExtractedFieldsForSection(selectedSection).length,
      aiPrefilled: getAIPrefilledFieldsForSection(selectedSection).length,
      errors: getSectionErrorCount(selectedSection),
      optional: getOptionalFieldsForSection(selectedSection).length,
    };
    if (stickyFieldPaths.size === 0) return counts;
    // Every count is a per-field tally for this section, so each pinned field
    // whose value has already moved it elsewhere shifts exactly one unit back
    // to the tab it is being edited in.
    getFieldsForSection(selectedSection).forEach(field => {
      if (!stickyFieldPaths.has(field.path)) return;
      const liveTab = classifyFieldTab(field.path);
      if (liveTab === activeFilter) return;
      counts[COUNT_KEY_FOR_TAB[liveTab]] = Math.max(0, counts[COUNT_KEY_FOR_TAB[liveTab]] - 1);
      counts[COUNT_KEY_FOR_TAB[activeFilter]] += 1;
    });
    return counts;
  }, [selectedSection, activeFilter, extractedData, fieldValidationErrors, mlPredictions, userEditedFields, stickyFieldPaths, classifyFieldTab]);

  // Get the filtered fields to render based on selectedSection + activeFilter
  const filteredFieldsToRender = useMemo((): AtlasField[] => {
    if (selectedSection === 'all_errors') {
      // Use frozen snapshot while user is editing; fall back to live list if no snapshot yet
      return withLiveErrors(frozenErrorFields['__all__'], allErrorFields);
    }
    const sectionFields = getFieldsForSection(selectedSection);
    // Fields the user is editing stay put until they leave them (see sticky block)
    const withSticky = (live: AtlasField[]) => mergeStickyFields(live, sectionFields, stickyFieldPaths);
    switch (activeFilter) {
      case 'extracted':
        return withSticky(getExtractedFieldsForSection(selectedSection));
      case 'ai_prefilled':
        return withSticky(getAIPrefilledFieldsForSection(selectedSection));
      case 'errors':
        // Use frozen snapshot so fields don't vanish while the user fills them in
        return withSticky(withLiveErrors(frozenErrorFields[selectedSection], getErrorFieldsForSection(selectedSection)));
      case 'optional':
        return withSticky(getOptionalFieldsForSection(selectedSection));
      default:
        return sectionFields;
    }
  }, [selectedSection, activeFilter, extractedData, fieldValidationErrors, mlPredictions, frozenErrorFields, userEditedFields, stickyFieldPaths]);

  // Total fields selected count
  const totalFieldsCount = atlasFields.length;
  const totalFieldsSelected = atlasFields.filter(field => {
    const value = memoizedGetNestedValue(extractedData, field.path);
    return value !== null && value !== undefined && value !== '';
  }).length;


  return (
    <Box ref={formContainerRef} sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 'calc(100vh - 280px)', position: 'relative' }}>

      {/* Connector arrow: a left-pointing arrow (form-panel background color) sitting in
          the gap, aligned to the active nav item, visually linking the form panel back
          to its section. */}
      {connectorTop !== null && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            // Nav rail is 280px + 16px gap; arrow base meets the panel edge (296),
            // tip points left into the gap toward the active nav item.
            left: 287,
            top: connectorTop,
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '7px solid transparent',
            borderBottom: '7px solid transparent',
            // Match the form panel background so the arrow reads as the panel
            // extending a pointer toward the active section.
            borderRight: '9px solid #f7f7f7',
            transition: 'top 0.2s ease',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* ═══ LEFT SECTION NAVIGATION (Vertical rail) + label ═══ */}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: 280, minWidth: 280, flexShrink: 0, gap: 1 }}>
      <Box
        sx={{
          width: '100%',
          flex: 1,
          bgcolor: '#f7f7f7',
          border: `1px solid ${brand.borderSubtle}`,
          borderRadius: 1,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Section list */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          {sections.map((section) => {
            const completeness = getSectionCompleteness(section.name);
            const sectionErrorCount = getSectionErrorCount(section.name);
            const isActive = selectedSection === section.name;

            return (
              <Box
                key={section.name}
                data-testid={`section-nav-${section.name}`}
                onClick={() => {
                  setSelectedSection(section.name);
                  setActiveFilter(sectionErrorCount > 0 ? 'errors' : 'extracted');
                }}
                ref={(el: any) => { sectionRefs.current[section.name] = el as HTMLDivElement | null; }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  position: 'relative',
                  // Active item: raised white card. The link to the form panel is
                  // drawn by the connector arrow on the form panel's left edge.
                  border: '1px solid',
                  borderColor: isActive ? brand.borderSubtle : 'transparent',
                  backgroundColor: isActive ? '#FFFFFF' : 'transparent',
                  boxShadow: isActive ? '0px 1px 4px rgba(0, 0, 0, 0.10)' : 'none',
                  borderRadius: 1,
                  '&:hover': { backgroundColor: isActive ? '#FFFFFF' : '#FAFAFA' },
                  mb: 0.5,
                  transition: 'all 0.15s ease',
                }}
              >
                {/* Row: icon + title + badge + error dot */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NavIcon src={ICON_FOR_SECTION(section.name, tString(section.titleKey))} size={24} />
                  <Box
                    sx={{
                      flex: 1,
                      fontSize: 12.5,
                      fontWeight: isActive ? 600 : 500,
                      color: brand.textPrimary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {tString(section.titleKey)}
                  </Box>
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: completeness.percentage === 100 ? '#E8F5E9' : isActive ? brand.orange : '#E0E0E0',
                      color: completeness.percentage === 100 ? '#2E7D32' : isActive ? '#fff' : brand.textSecondary,
                      minWidth: 'auto',
                      textAlign: 'center',
                      lineHeight: 1.4,
                      flexShrink: 0,
                    }}
                  >
                    {completeness.completed}/{completeness.total}
                  </Box>
                  {sectionErrorCount > 0 && !isActive && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: brand.error,
                        flexShrink: 0,
                      }}
                      aria-label={tString('atlasFields.errorCount', sectionErrorCount)}
                    />
                  )}
                </Box>
                {/* Green progress bar */}
                <Box sx={{ mt: 0.75, width: '100%', height: 3, borderRadius: 999, bgcolor: '#E0E0E0', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${completeness.percentage}%`,
                      borderRadius: 999,
                      bgcolor: completeness.percentage === 100 ? '#4CAF50' : completeness.percentage > 0 ? '#66BB6A' : 'transparent',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </Box>
              </Box>
            );
          })}

          {/* All Errors virtual section */}
          {(() => {
            const isAllErrorsActive = selectedSection === 'all_errors';
            return (
              <Box
                onClick={() => {
                  setSelectedSection('all_errors');
                  setActiveFilter('errors');
                }}
                ref={(el: any) => { sectionRefs.current['all_errors'] = el as HTMLDivElement | null; }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  position: 'relative',
                  // Active item: raised white card; top border stays as the separator
                  // from the regular sections above. Connector lives on the form panel.
                  backgroundColor: isAllErrorsActive ? '#FFFFFF' : 'transparent',
                  boxShadow: isAllErrorsActive ? '0px 1px 4px rgba(0, 0, 0, 0.10)' : 'none',
                  border: '1px solid',
                  borderColor: isAllErrorsActive ? brand.borderSubtle : 'transparent',
                  borderTopColor: brand.borderSubtle,
                  borderRadius: 1,
                  '&:hover': { backgroundColor: isAllErrorsActive ? '#FFFFFF' : '#FAFAFA' },
                  mt: 1,
                  pt: 1.5,
                  transition: 'all 0.15s ease',
                }}
              >
                <NavIcon src={formIcons.allErrors} size={24} />
                <Box
                  sx={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: isAllErrorsActive ? 600 : 500,
                    color: totalErrorCount > 0 ? brand.error : brand.textSecondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {tString('atlasFields.allErrors') || 'All Errors'}
                </Box>
                {totalErrorCount > 0 && (
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: isAllErrorsActive ? brand.orange : brand.error,
                      color: '#fff',
                      minWidth: 28,
                      textAlign: 'center',
                      lineHeight: 1.4,
                    }}
                  >
                    {totalErrorCount}
                  </Box>
                )}
              </Box>
            );
          })()}
        </Box>

        {/* Fields selected count — inside the sidebar box at the bottom */}
        <Typography sx={{ fontSize: '0.72rem', color: brand.textPrimary, px: 1.5, py: 1, borderTop: `1px solid ${brand.borderSubtle}`, flexShrink: 0 }}>
          {totalFieldsSelected} of {totalFieldsCount} fields selected
        </Typography>

      </Box>

      </Box>

      {/* ═══ RIGHT FORM CONTENT AREA ═══ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 280,
          bgcolor: '#f7f7f7',
          border: `1px solid ${brand.borderSubtle}`,
          borderRadius: 1,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Filter Tabs bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${brand.borderSubtle}`,
            px: 2,
            bgcolor: '#f7f7f700',
          }}
        >
          {selectedSection !== 'all_errors' && (() => {
            const FILTER_TABS: Array<{
              key: 'extracted' | 'ai_prefilled' | 'errors' | 'optional';
              label: string;
              count: number;
            }> = [
              { key: 'extracted', label: tString('atlasFields.filterTabs.extracted') || 'Extracted', count: filterCounts.extracted },
              { key: 'ai_prefilled', label: tString('atlasFields.filterTabs.aiPrefilled') || 'AI Prefilled', count: filterCounts.aiPrefilled },
              { key: 'errors', label: tString('atlasFields.filterTabs.errors') || 'Errors', count: filterCounts.errors },
              { key: 'optional', label: tString('atlasFields.filterTabs.otherFields') || 'Other Fields', count: filterCounts.optional },
            ];
            return (
              <>
                {FILTER_TABS.map(tab => {
                  const isActive = activeFilter === tab.key;
                  return (
                    <Box
                      key={tab.key}
                      data-testid={`filter-tab-${tab.key}`}
                      onClick={() => setActiveFilter(tab.key)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? brand.textPrimary : brand.textSecondary,
                        borderBottom: isActive ? `2px solid ${brand.orange}` : '2px solid transparent',
                        mb: '-1px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s ease',
                        '&:hover': { color: brand.textPrimary },
                      }}
                    >
                      {tab.label} ({tab.count})
                    </Box>
                  );
                })}
              </>
            );
          })()}
          {selectedSection === 'all_errors' && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                fontSize: 13,
                fontWeight: 600,
                color: brand.error,
                borderBottom: `2px solid ${brand.orange}`,
                mb: '-1px',
              }}
            >
              Errors ({totalErrorCount})
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          {headerActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {headerActions}
            </Box>
          )}
        </Box>

        {/* Scrollable form fields area.
            onFocus/onBlur are delegated here (they bubble) so every input kind —
            text field, codelist autocomplete, multi-select — pins the field being
            edited to the active tab and releases it when the user leaves it. */}
        <Box
          sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f7f7f700' }}
          onFocus={handleFieldAreaFocus}
          onBlur={handleFieldAreaBlur}
        >
          {(selectedSection === 'kopf' || selectedSection === 'anmelder') && (() => {
            // Grouped, tab-aware rendering: every cluster gets its own always-open fieldset.
            // A group box shows only when it has at least one field in the active filter tab,
            // so a field appears in exactly one tab and a box leaves a tab once it's empty there.
            const groups = groupFieldsForSection(selectedSection, filteredFieldsToRender);
            const visible = groups.filter(g => g.fields.length > 0);
            if (visible.length === 0) {
              return (
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">
                    {activeFilter === 'errors'
                      ? 'No validation errors in this section.'
                      : activeFilter === 'ai_prefilled'
                      ? 'No AI-prefilled fields in this section.'
                      : activeFilter === 'extracted'
                      ? 'No extracted data found in this section.'
                      : 'No other fields in this section.'}
                  </Typography>
                </Box>
              );
            }
            return visible.map(g => {
              const label = tString(g.labelKey);
              return (
                <FieldGroup key={g.id} title={label === g.labelKey ? g.fallbackLabel : label}>
                  {g.fields.map(field => renderFieldWithMLPrediction(field))}
                </FieldGroup>
              );
            });
          })()}
          {(() => {
            // ── Position section: filter-aware line-item rendering ──
            if (selectedSection === 'position') {
              // All position fields (for total item count + description lookup)
              const allPosFields = getFieldsForSection('position');
              const allPosIndices = Array.from(new Set(
                allPosFields.map(f => { const m = f.path.match(/position\[(\d+)\]/); return m ? m[1] : '0'; })
              )).sort((a, b) => parseInt(a) - parseInt(b));

              // Filtered position fields based on active tab
              const filteredPosFields = filteredFieldsToRender; // already filtered by activeFilter

              // Group filtered fields by item index
              const positionGroups: Record<string, AtlasField[]> = {};
              filteredPosFields.forEach(field => {
                const match = field.path.match(/position\[(\d+)\]/);
                const posIndex = match ? match[1] : '0';
                if (!positionGroups[posIndex]) positionGroups[posIndex] = [];
                positionGroups[posIndex].push(field);
              });

              const positionIndices = Object.keys(positionGroups).sort((a, b) => parseInt(a) - parseInt(b));

              if (positionIndices.length === 0) {
                return (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">
                      {activeFilter === 'errors'
                        ? 'No validation errors in line items.'
                        : activeFilter === 'ai_prefilled'
                        ? 'No AI predictions available for line items.'
                        : activeFilter === 'extracted'
                        ? 'No extracted data found in line items.'
                        : 'No other fields in line items.'}
                    </Typography>
                  </Box>
                );
              }

              return (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, p: 0.75, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.12) }}>
                    <InventoryIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="caption" fontWeight={500} color="primary.main" sx={{ fontSize: '0.75rem' }}>
                      {allPosIndices.length} {allPosIndices.length === 1 ? 'Item' : 'Items'}
                    </Typography>
                  </Box>

                  {positionIndices.map((posIndex) => {
                    const itemFields = positionGroups[posIndex];
                    const expandKey = activeFilter === 'optional' ? `opt_${posIndex}` : posIndex;
                    const isItemExpanded = expandedLineItems[expandKey] || false;
                    // Look up description from full item data (not just filtered subset)
                    const descPath = `position[${posIndex}].wareWarenbezeichnung`;
                    const descValue = memoizedGetNestedValue(extractedData, descPath) || '';
                    const summaryLabel = descValue
                      ? String(descValue).substring(0, 50) + (String(descValue).length > 50 ? '...' : '')
                      : '';

                    return (
                      <Box key={posIndex} sx={{ mb: 1.5, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.15), borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), overflow: 'hidden' }}>
                        <Box
                          data-testid={`line-item-toggle-${posIndex}`}
                          onClick={() => setExpandedLineItems(prev => ({ ...prev, [expandKey]: !prev[expandKey] }))}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, cursor: 'pointer', borderBottom: isItemExpanded ? '2px solid' : 'none', borderColor: alpha(theme.palette.success.main, 0.3), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}
                        >
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0 }}>
                            {parseInt(posIndex) + 1}
                          </Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary', flex: 1 }}>
                            Item {parseInt(posIndex) + 1} of {allPosIndices.length}
                            {summaryLabel && (
                              <Typography component="span" sx={{ fontWeight: 400, fontSize: '0.8rem', color: 'text.secondary', ml: 1 }}>— {summaryLabel}</Typography>
                            )}
                          </Typography>
                          <Chip label={`${itemFields.length} fields`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary', transform: isItemExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                        </Box>

                        <Collapse in={isItemExpanded} timeout={200}>
                          <Box sx={{ p: 1.5 }}>
                            <Grid container spacing={0.5}>
                              {itemFields.map(field => renderFieldWithMLPrediction(field))}
                            </Grid>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </>
              );
            }

            // ── All Errors virtual section: grouped by section with headers ──
            if (selectedSection === 'all_errors') {
              // Use frozen snapshot so fields don't disappear while the user is
              // actively filling them in (onClearFieldError fires on every blur).
              // Falls back to live list only before the first snapshot is taken.
              const errorFieldsToShow = withLiveErrors(frozenErrorFields['__all__'], allErrorFields);

              // "All fields pass validation" may only be said when NOTHING is
              // outstanding — including errors with no field to attach to.
              if (errorFieldsToShow.length === 0 && orphanErrors.length === 0) {
                return (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="body1" fontWeight={600}>{tString('atlasFields.noValidationErrors')}</Typography>
                    <Typography variant="body2" color="text.secondary">{tString('atlasFields.allFieldsValid')}</Typography>
                  </Box>
                );
              }

              // Build section groups from the frozen snapshot — same fields stay
              // visible regardless of intermediate re-validations while editing.
              const sectionGroups = sections
                .map(section => ({
                  section,
                  fields: errorFieldsToShow.filter(f => f.section === section.name),
                }))
                .filter(g => g.fields.length > 0);

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Errors ATLAS reported against a path this form has no
                      control for. Shown with the raw path so they are at least
                      actionable, instead of inflating a count with nothing
                      behind it. */}
                  {orphanErrors.length > 0 && (
                    <Box
                      component="fieldset"
                      sx={{
                        border: '1px solid',
                        borderColor: alpha(theme.palette.error.main, 0.3),
                        borderRadius: 2,
                        p: 1.5,
                        m: 0,
                      }}
                    >
                      <Typography
                        component="legend"
                        variant="caption"
                        sx={{ px: 0.5, fontWeight: 700, color: 'error.main' }}
                      >
                        {`Reported by ATLAS (${orphanErrors.length}) — no matching field on this form`}
                      </Typography>
                      {orphanErrors.map(({ path, message }) => (
                        <Box key={path} sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, display: 'block', wordBreak: 'break-word' }}
                          >
                            {path}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="error.main"
                            sx={{ display: 'block', wordBreak: 'break-word' }}
                          >
                            {message}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {sectionGroups.map(({ section, fields }) => (
                    <Box
                      key={section.name}
                      component="fieldset"
                      sx={{
                        border: '1px solid',
                        borderColor: alpha(theme.palette.error.main, 0.3),
                        borderRadius: 2,
                        p: 0,
                        m: 0,
                        pt: 1,
                      }}
                    >
                      {/* Section header — fieldset legend style */}
                      <Box
                        component="legend"
                        sx={{
                          ml: 1.5,
                          px: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          lineHeight: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            color: theme.palette.text.primary,
                          }}
                        >
                          {tString(section.titleKey)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: theme.palette.error.main,
                            fontWeight: 600,
                          }}
                        >
                          ({fields.length})
                        </Typography>
                      </Box>

                      {/* Fields grid */}
                      <Box sx={{ p: 1.5, pt: 0.5 }}>
                        <Grid container spacing={0.5}>
                          {fields.map(field => renderFieldWithMLPrediction(field))}
                        </Grid>
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            }

            // ── Regular sections (ausfuhrer, empfaenger): flat grid ──
            // kopf and anmelder are rendered by the grouped block above and handled there.
            if (selectedSection === 'kopf' || selectedSection === 'anmelder') return null;
            const fieldsToRender = filteredFieldsToRender;

            if (fieldsToRender.length === 0) {
              return (
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">
                    {activeFilter === 'errors'
                      ? 'No validation errors in this section.'
                      : activeFilter === 'ai_prefilled'
                      ? 'No AI-prefilled fields in this section.'
                      : 'No optional fields in this section.'}
                  </Typography>
                </Box>
              );
            }

            return (
              <Grid container spacing={0.5}>
                {fieldsToRender.map(field => renderFieldWithMLPrediction(field))}
              </Grid>
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
};

export default AtlasStructuredForm;
